import { useRecoilValue } from 'recoil';
import { integrationAtom } from '../atom';
import { Box, CircularProgress, SxProps, Theme } from '@mui/material';
import { useState } from 'react';
import { getAtsCandidateUrl } from '../utils/getAtsCandidateUrl';
import { useGetCandidateProfileAtsApplicationIdLazyQuery } from 'generated-graphql/hooks/apollo-hooks';
import { extractQueryGetItem } from 'core/utils/apollo/extractGetItem';

const profileLink: SxProps<Theme> = {
  color: 'primary.main',
  cursor: 'pointer',
  textDecoration: 'underline'
};

// Names the link for a candidate whose record lives in the caller's own system rather than
// in an ATS, where an id from either would mean nothing to the reader.
const EXTERNAL_PROFILE_LINK_LABEL = 'View profile';

type Props = {
  candidateProfileId: string | null | undefined;
  atsCandidateId: string | null | undefined;
  externalUrl: string | null | undefined;
};

export const AtsCandidateProfileLink: React.FC<Props> = ({
  candidateProfileId,
  atsCandidateId,
  externalUrl
}) => {
  const integrationCredentials = useRecoilValue(integrationAtom);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchCandidateProfile] =
    useGetCandidateProfileAtsApplicationIdLazyQuery();

  // An ATS record needs both the credentials that build the URL and the id it points at. A
  // candidate created through the public API has neither, so the link the caller stored on
  // the profile is all there is to open.
  const hasAtsRecord = Boolean(
    integrationCredentials && candidateProfileId && atsCandidateId
  );

  const handleClick = async () => {
    if (!hasAtsRecord || !candidateProfileId) {
      if (externalUrl) {
        window.open(externalUrl, '_blank');
      }
      return;
    }

    setIsLoading(true);

    try {
      const { data } = await fetchCandidateProfile({
        variables: { id: candidateProfileId }
      });

      const candidateProfile = extractQueryGetItem(data);
      const latestAtsApplicationId = candidateProfile?.latestAtsApplicationId;

      const url = getAtsCandidateUrl({
        integrationCredentials,
        atsCandidateId,
        atsApplicationId: latestAtsApplicationId
      });

      // For an ATS-sourced candidate the ATS record is the one the user expects, so the
      // caller's own link only stands in when that URL cannot be built.
      const target = url || externalUrl;

      if (target) {
        window.open(target, '_blank');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!hasAtsRecord && !externalUrl) {
    return null;
  }

  if (isLoading) {
    return <CircularProgress size={16} />;
  }

  return (
    <Box sx={profileLink} onClick={handleClick}>
      {hasAtsRecord ? atsCandidateId : EXTERNAL_PROFILE_LINK_LABEL}
    </Box>
  );
};
